import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStory } from '../../hooks/useStory';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import Chapter from './components/Chapter';
import Rail from './components/Rail';
import Hero, { HeroAction } from './chapters/Hero';
import Allocation from './chapters/Allocation';
import Trend from './chapters/Trend';
import Comparison from './chapters/Comparison';
import Performers from './chapters/Performers';
import Contributions from './chapters/Contributions';
import Dividends from './chapters/Dividends';
import Insight from './chapters/Insight';
import Close from './chapters/Close';
import './story.css';

/**
 * Portfolio Story — the post-login scroll summary.
 *
 * Nine viewport-height chapters on a snapping scroller, rendered outside
 * `AppLayout` (no sidebar, no header). Implements the design handoff in
 * `docs/design_handoff_portfolio_story/`; see
 * `.claude/docs/tasks/portfolio-story/context.md` for the decisions behind it.
 *
 * Two structural notes:
 *
 *  - The chapter list is computed, not hard-coded. Chapter 08 (the AI insight
 *    poster) is dropped entirely when the user has no insights, and the counters
 *    and rail both renumber off the resulting list.
 *  - The scroller is this component, not the document. Chapter navigation uses
 *    `scrollTo` on that element rather than `scrollIntoView`, which would fight
 *    the app's router and any surrounding chrome.
 */
function Story() {
  const { story } = useStory();
  const prefersReducedMotion = usePrefersReducedMotion();
  const scrollerRef = useRef(null);
  const chapterRefs = useRef([]);
  const [active, setActive] = useState(1);

  const hasInsight = Boolean(story?.latestInsight?.text);

  // Every chapter that will actually render, in order. `Chapter` receives its
  // position and the total from here, so dropping one renumbers the rest.
  const chapters = useMemo(() => {
    const list = [
      { key: 'hero', tag: 'ALL TIME', tagVariant: 'accent', label: 'Hero', render: (s) => <Hero story={s} />, action: <HeroAction /> },
      { key: 'allocation', tag: 'ALLOCATION', label: 'Allocation', render: (s) => <Allocation story={s} /> },
      { key: 'trend', tag: 'TREND', label: 'Trend', render: (s) => <Trend story={s} /> },
      { key: 'comparison', tag: 'COMPARISON', label: 'Comparison against the market', render: (s) => <Comparison story={s} /> },
      { key: 'performers', tag: 'PERFORMERS', label: 'Best and worst performers', render: (s) => <Performers story={s} /> },
      { key: 'contributions', tag: 'CONTRIBUTIONS', label: 'Contributions versus market gains', render: (s) => <Contributions story={s} /> },
      { key: 'dividends', tag: 'INCOME', label: 'Dividends', render: (s) => <Dividends story={s} /> },
    ];

    if (hasInsight) {
      list.push({
        key: 'insight',
        tag: "TODAY'S INSIGHT",
        tagVariant: 'outline-light',
        className: 'story-chapter-poster',
        label: "Today's insight",
        render: (s) => <Insight story={s} />,
      });
    }

    return list;
  }, [hasInsight]);

  const scrollToChapter = useCallback((index) => {
    const scroller = scrollerRef.current;
    const target = chapterRefs.current[index - 1];

    if (!scroller || !target) {
      return;
    }

    scroller.scrollTo({
      top: target.offsetTop,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, [prefersReducedMotion]);

  const total = chapters.length + 1; // + the closing chapter

  // Track the chapter filling the viewport so the rail can mark it.
  useEffect(() => {
    const sections = chapterRefs.current.filter(Boolean);

    if (sections.length === 0 || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.chapter);
            if (index) {
              setActive(index);
            }
          }
        });
      },
      { root: scrollerRef.current, threshold: 0.5 }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, [total]);

  return (
    <div className="story-root" ref={scrollerRef} data-testid="story-root">
      <Rail count={total} active={active} onSelect={scrollToChapter} />

      {chapters.map((chapter, i) => (
        <Chapter
          key={chapter.key}
          id={`story-chapter-${i + 1}`}
          index={i + 1}
          total={total}
          tag={chapter.tag}
          tagVariant={chapter.tagVariant}
          className={chapter.className}
          label={chapter.label}
          action={chapter.action}
          ref={(el) => { chapterRefs.current[i] = el; }}
        >
          {chapter.render(story)}
        </Chapter>
      ))}

      <Chapter
        id={`story-chapter-${total}`}
        index={total}
        total={total}
        tag="END"
        label="Close"
        ref={(el) => { chapterRefs.current[total - 1] = el; }}
      >
        <Close onBackToTop={() => scrollToChapter(1)} />
      </Chapter>
    </div>
  );
}

export default Story;
